'use client'

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { IChildrenProps } from '@/utils/typings/children-props'
import { noOp } from '@/utils/functions/no-op'
import { LOCAL_STORAGE_KEYS } from '@/setup/local-storage'
import { useMediaQuery } from '@/utils/hooks/use-media-query'
import { useGetBrandsQuery } from '@/api/brands/queries/get-brands-query'
import { useIsUserInOnboarding } from '@/features/onboarding/hooks/use-is-user-in-onboarding'
import { usePostOnboardingPromptStore } from '@/features/onboarding/stores/post-onboarding-prompt-store'
import { computeIsOnboardingSidebarLocked } from '@/features/onboarding/onboarding-sidebar-lock'
import { useOnboardingChatRecovery } from '@/features/onboarding/hooks/use-onboarding-chat-recovery'
import type { IOnboardingRecoveryStatus } from '@/features/onboarding/utils/onboarding-chat-recovery'

type ISidebarState = 'expanded' | 'collapsed'

interface ISidebarContext {
    state: ISidebarState
    toggleSidebar: () => void
    setState: (state: ISidebarState) => void
    isSmallScreen: boolean
    /** True when the collapsed sidebar is temporarily expanded as an overlay (hover) */
    isFloating: boolean
    setIsFloating: (floating: boolean) => void
    /** Ref-based flag — set synchronously to prevent the next float from triggering.
     *  Used by the expand toggle so hovering it doesn't cause a temporary expand. */
    floatPreventedRef: React.RefObject<boolean>
    /** Ref-based flag — true while a dropdown/popover inside the sidebar is open.
     *  Prevents the floating sidebar from closing on mouse-leave. */
    interactionLockRef: React.RefObject<boolean>
    isMobileSidebarOpen: boolean
    setMobileSidebarOpen: (open: boolean) => void
    /** True when the user has no real (non-empty) brand — sidebar is locked collapsed */
    isSidebarLocked: boolean
    /** Current brand's existing onboarding chat, when available. */
    onboardingChatUrl: string | null
    canRecoverOnboarding: boolean
    openOnboardingChat: () => Promise<void>
    onboardingRecoveryStatus: IOnboardingRecoveryStatus
}

export const SidebarContext = createContext<ISidebarContext>({
    state: 'expanded',
    toggleSidebar: noOp,
    setState: noOp,
    isSmallScreen: false,
    isFloating: false,
    setIsFloating: noOp,
    floatPreventedRef: { current: false },
    interactionLockRef: { current: false },
    isMobileSidebarOpen: false,
    setMobileSidebarOpen: noOp,
    isSidebarLocked: false,
    onboardingChatUrl: null,
    canRecoverOnboarding: false,
    openOnboardingChat: async () => {},
    onboardingRecoveryStatus: 'idle',
})

const guardIsSidebarState = (state: string | null): state is ISidebarState => {
    return state === 'expanded' || state === 'collapsed'
}

type ISidebarProviderProps = IChildrenProps

const SIDEBAR_KEYBOARD_SHORTCUT = 'b'

export function SidebarProvider({ children }: ISidebarProviderProps) {
    const isSmallScreen = useMediaQuery('(max-width: 1240px)') // @aron: finger weg

    const { data: brands } = useGetBrandsQuery()
    const isUserInOnboarding = useIsUserInOnboarding()

    // Capture the initial default once so that mid-session brand creation doesn't auto-expand.
    const initialDefaultRef = useRef<ISidebarState | null>(null)
    if (initialDefaultRef.current === null && brands !== undefined) {
        initialDefaultRef.current = !isUserInOnboarding ? 'expanded' : 'collapsed'
    }

    // Locked while the user has no real brand yet, AND through the first brand's complete
    // questionnaire (including its client-only task-launch step), which runs after the brand is
    // created. The live-session flag keeps that final step from unlocking the sidebar early.
    const isOnboardingFlowActive = usePostOnboardingPromptStore((s) => s.isOnboardingFlowActive)
    const isFirstBrand = !!brands && brands.length <= 1
    // The brand's own record of whether onboarding finished. Durable, so it survives the reloads
    // that used to release the lock — and it is what stops a persisted `isOnboardingFlowActive`
    // from outliving real onboarding.
    const hasCompletedOnboardingBrand = !!brands && brands.some((brand) => brand.onboardingCompleted)
    // Gate on the brands query having resolved: `useIsUserInOnboarding` reads
    // `!(brands?.some(...) ?? false)`, which is `true` while `brands` is still undefined. Since the
    // lock hard-collapses the sidebar (below), an unresolved list would otherwise collapse it for
    // every user on every cold load, new tab, and org switch. Shared with the deep-link resolver so
    // the two never disagree — see computeIsOnboardingSidebarLocked.
    const isSidebarLocked = computeIsOnboardingSidebarLocked({
        brandsLoaded: brands !== undefined,
        hasCompletedOnboardingBrand,
        isUserInOnboarding,
        isFirstBrand,
        isOnboardingFlowActive,
    })

    const [rawState, setState] = useState<ISidebarState | null>(null)
    const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false)
    const [rawIsFloating, setIsFloating] = useState(false)
    const onboardingRecovery = useOnboardingChatRecovery(isSidebarLocked)
    const floatPreventedRef = useRef(false)
    const interactionLockRef = useRef(false)

    const resolvedState = useMemo(() => {
        if (rawState) return rawState

        // Sidebar state should be user-controlled (and persisted), not forced by viewport width.
        // Small screens can use the mobile drawer without collapsing the desktop sidebar state.
        try {
            const storedState = localStorage.getItem(LOCAL_STORAGE_KEYS.Sidebar.State)
            if (guardIsSidebarState(storedState)) return storedState
        } catch {
            // noop: localStorage may be unavailable in some environments
        }

        return initialDefaultRef.current ?? 'collapsed'
    }, [rawState])

    // A locked sidebar is hard-collapsed at the source. Every consumer derives
    // `isExpanded = state === 'expanded' || isFloating`, so gating BOTH values here is what makes
    // the lock total — blocking only `toggleSidebar` still left two ways in: a persisted
    // `sidebar_state = 'expanded'` (the key is global, so it survives into a new org's onboarding)
    // and the hover-float overlay in `SidebarLayout`.
    const state = isSidebarLocked ? 'collapsed' : resolvedState
    const isFloating = isSidebarLocked ? false : rawIsFloating

    const handleSetState = useCallback((newState: ISidebarState) => {
        setState(newState)
        if (newState === 'expanded') setIsFloating(false)
        localStorage.setItem(LOCAL_STORAGE_KEYS.Sidebar.State, newState)
    }, [])

    const handleToggleSidebar = useCallback(() => {
        if (isSidebarLocked) return
        handleSetState(state === 'expanded' ? 'collapsed' : 'expanded')
    }, [state, isSidebarLocked, handleSetState])

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
                event.preventDefault()
                handleToggleSidebar()
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleToggleSidebar])

    return (
        <SidebarContext.Provider
            value={{
                state,
                toggleSidebar: handleToggleSidebar,
                setState: handleSetState,
                isSmallScreen: Boolean(isSmallScreen),
                isFloating,
                setIsFloating,
                floatPreventedRef,
                interactionLockRef,
                isMobileSidebarOpen,
                setMobileSidebarOpen,
                isSidebarLocked,
                ...onboardingRecovery,
            }}
        >
            {children}
        </SidebarContext.Provider>
    )
}

export function useSidebarContext() {
    return useContext(SidebarContext)
}
